import {
  newbeeRookieTagIdsByJobFilterType,
  NEWBEE_ROOKIE_EXCLUDED_LOCATION_IDS,
} from "@/constants/misc";
import { filter, get, includes, some } from "lodash";

export const tags = {
  1: { class: "", emoji: "🕺" },
  2: { class: "text-green-500", emoji: "" },
  3: { class: "", emoji: "💪" },
  5: { class: "", emoji: "🔴" },
  10: { class: "text-yellow-500", emoji: "" },
  11: { class: "text-purple-500", emoji: "" },
  13: { class: "", emoji: "❗" },
  14: { class: "", emoji: "🆕" },
  15: { class: "", emoji: "👍" },
  // Job-filter-specific Newbee variants (mirror tag 14's emoji)
  19: { class: "", emoji: "🆕" }, // Newbee - Afwas (Dishes)
  20: { class: "", emoji: "🆕" }, // Newbee - Bediening (Non Kitchen)
  21: { class: "", emoji: "🆕" }, // Newbee - Keuken (Kitchen)
  // Job-filter-specific Rookie variants (mirror tag 15's emoji)
  22: { class: "", emoji: "👍" }, // Rookie - Afwas (Dishes)
  23: { class: "", emoji: "👍" }, // Rookie - Bediening (Non Kitchen)
  24: { class: "", emoji: "👍" }, // Rookie - Keuken (Kitchen)
};

export const getTag = (tags) => new Tag(tags);

export const getTagStyle = (id) => get(tags, `${id}.class`, "");

export const getTagEmoji = (id) => get(tags, `${id}.emoji`, "");

// All job-filter-specific Newbee/Rookie tag ids (19-24), derived from the
// configured pairs, used to strip them out wholesale when they can't be
// safely narrowed down to a single active job filter (see below).
const allNewbeeRookieVariantTagIds = Object.values(
  newbeeRookieTagIdsByJobFilterType
).flatMap((pair) => [pair.newbee, pair.rookie]);

/**
 * Filters the global Newbee (14) / Rookie (15) tags down to the job-filter
 * specific variants (19-24) that should be shown on the /main and
 * /new-rooster screens, based on the currently active job filter.
 *
 * Horeca locations listed in NEWBEE_ROOKIE_EXCLUDED_LOCATION_IDS keep the
 * legacy global 14/15 behavior: any job-filter-specific variant ids
 * (19-24) are stripped out (in case the upstream tag source also emits
 * them for this location), then 14/15 are deduped (if both 14 and 15 are
 * present, only 14 is kept).
 *
 * For all other locations:
 * - Tags 14 and 15 are stripped entirely.
 * - Only the newbee/rookie pair matching the active job filter's `type_id`
 *   is kept (e.g. 19/22 for Dishes, 20/23 for Non Kitchen, 21/24 for
 *   Kitchen). Job filter `name` is location-specific free text and is NOT
 *   used for matching - `type_id` is the value that's consistent across
 *   locations.
 * - If both the newbee and rookie id of that pair are present, only the
 *   newbee id is kept (mirrors the 14/15 dedup behavior).
 * - If the active job filter's type_id can't be resolved to a known pair
 *   (e.g. missing/unrecognized type_id), ALL job-filter-specific
 *   newbee/rookie tag ids (19-24) are stripped as a safe fallback, so no
 *   employee ends up showing an incorrect/unfiltered newbee/rookie tag.
 * - Any other (unrelated) tag ids are left untouched.
 *
 * @param {Array|undefined} tags
 * @param {number|string|undefined} jobFilterTypeId
 * @param {number|string|undefined} horecaLocationId
 * @returns {Array|undefined}
 */
export const filterNewbeeRookieTags = (
  tags,
  jobFilterTypeId,
  horecaLocationId
) => {
  if (includes(NEWBEE_ROOKIE_EXCLUDED_LOCATION_IDS, +horecaLocationId)) {
    // Some employee tag data now contains BOTH the legacy tag (14/15) AND
    // the job-filter-specific variant (19-24) for the same employee, since
    // the upstream system that supplies tags isn't aware of this per-location
    // exclusion. Strip the new variant ids here too, so excluded locations
    // only ever show the legacy global 14/15 tags.
    const withoutNewVariants = filter(
      tags,
      (t) => !includes(allNewbeeRookieVariantTagIds, t.tagid)
    );

    const has14 = some(withoutNewVariants, (t) => t.tagid === 14);
    const has15 = some(withoutNewVariants, (t) => t.tagid === 15);

    if (has14 && has15) {
      return filter(withoutNewVariants, (t) => t.tagid !== 15);
    }

    return withoutNewVariants;
  }

  const withoutGlobalTags = filter(
    tags,
    (t) => t.tagid !== 14 && t.tagid !== 15
  );

  const pair = newbeeRookieTagIdsByJobFilterType[jobFilterTypeId];

  if (!pair) {
    // Can't safely resolve which pair applies - strip all newbee/rookie
    // variant ids rather than let them pass through unfiltered.
    return filter(
      withoutGlobalTags,
      (t) => !includes(allNewbeeRookieVariantTagIds, t.tagid)
    );
  }

  const hasNewbee = some(withoutGlobalTags, (t) => t.tagid === pair.newbee);
  const hasRookie = some(withoutGlobalTags, (t) => t.tagid === pair.rookie);

  return filter(withoutGlobalTags, (t) => {
    if (includes(allNewbeeRookieVariantTagIds, t.tagid)) {
      if (t.tagid === pair.rookie) {
        return hasRookie && !hasNewbee;
      }

      if (t.tagid === pair.newbee) {
        return hasNewbee;
      }

      // Belongs to a different job filter's newbee/rookie pair - strip it.
      return false;
    }

    return true;
  });
};

class Tag {
  tagPriorityOrder = [2, 3, 10, 11, 5, 1, 13];
  tags = [];

  constructor(tags) {
    this.tags = tags;
  }

  get tag() {
    if (!Array.isArray(this.tags) || this.tags.length === 0) {
      return null;
    }

    const tagIds = new Set(this.tags.map((t) => t.tagid));

    for (const id of this.tagPriorityOrder) {
      if (tagIds.has(id)) {
        const tag = this.tags.find((t) => t.tagid === id);

        return {
          ...tag,
          ...tags[id],
        };
      }
    }

    return null;
  }

  get style() {
    const tagWithStyle = this.getHighestPriorityTag("class");

    return tagWithStyle ? tagWithStyle.class : "";
  }

  get emoji() {
    const tagWithEmoji = this.getHighestPriorityTag("emoji");

    return tagWithEmoji ? tagWithEmoji.emoji : "";
  }

  hasTag(id) {
    if (!Array.isArray(this.tags) || this.tags.length === 0) {
      return false;
    }

    return this.tags.some((t) => t.tagid === id);
  }

  getHighestPriorityTag(prop) {
    if (!Array.isArray(this.tags) || this.tags.length === 0) {
      return null;
    }

    const tagIds = new Set(this.tags.map((t) => t.tagid));

    for (const id of this.tagPriorityOrder) {
      if (
        tagIds.has(id) &&
        tags[id] &&
        tags[id][prop] &&
        tags[id][prop].length > 0
      ) {
        return {
          ...this.tags.find((t) => t.tagid === id),
          ...tags[id],
        };
      }
    }

    return null;
  }
}
